import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext.js';
import { setExtensionId } from '../lib/extensionBridge.js';

type Status = 'signing-in' | 'sending' | 'ok' | 'err';

// Type augmentation for chrome.runtime in window scope.
declare global {
  interface Window { chrome?: any }
}

export function ExtensionAuth() {
  const { user, getToken, signIn, isDev } = useAuth();
  const [params] = useSearchParams();
  const extId = params.get('ext') ?? '';
  const apiBase = params.get('api') ?? (import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:8080');

  const [status, setStatus] = useState<Status>('signing-in');
  const [err, setErr] = useState<string | null>(null);
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (!extId) { setErr('Missing extension id. Open the extension popup and click Sign in again.'); setStatus('err'); return; }
    // Persist the extension id so onboarding's pingExtension() can find it on later visits.
    setExtensionId(extId);
    if (!user) { setStatus('signing-in'); return; }
    (async () => {
      try {
        setStatus('sending');
        const token = await getToken();
        if (!token) throw new Error('No session token available.');
        if (!window.chrome?.runtime?.sendMessage) {
          throw new Error('Open this page in Chrome (or a Chromium-based browser).');
        }
        await new Promise<void>((resolve, reject) => {
          window.chrome.runtime.sendMessage(
            extId,
            { type: 'tweetvia:setToken', token, apiBaseUrl: apiBase },
            (response: any) => {
              if (window.chrome.runtime.lastError) reject(new Error(window.chrome.runtime.lastError.message));
              else if (!response?.ok) reject(new Error('Extension did not acknowledge.'));
              else resolve();
            },
          );
        });
        setStatus('ok');
        setTimeout(() => window.close(), 1500);
      } catch (e: any) { setErr(e.message); setStatus('err'); }
    })();
    /* eslint-disable-next-line */
  }, [user]);

  async function doSignIn() {
    try { await signIn(isDev ? email : undefined); }
    catch (e: any) { setErr(e.message); setStatus('err'); }
  }

  return (
    <div className="landing-wrap">
      <div className="landing-inner" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div className="signin-card" style={{ textAlign: 'center', maxWidth: 420 }}>
          <h2 style={{ margin: '0 0 8px', fontSize: 20 }}>Connect tweetvia extension</h2>

          {status === 'signing-in' && !user && (
            <>
              <p className="muted small" style={{ margin: '0 0 16px' }}>
                {isDev ? 'Dev mode — enter any email to sign in.' : 'Sign in to give the extension access to your session.'}
              </p>
              {isDev && (
                <input
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  onKeyDown={e => { if (e.key === 'Enter' && email.includes('@')) doSignIn(); }}
                  autoFocus
                />
              )}
              <button className="primary big" style={{ marginTop: 8 }} onClick={doSignIn} disabled={isDev && !email.includes('@')}>
                Sign in
              </button>
            </>
          )}

          {status === 'sending' && <p className="muted small">Sending your session to the extension…</p>}

          {status === 'ok' && (
            <>
              <div className="status-row status-done" style={{ justifyContent: 'center' }}>
                <span className="status-icon">✓</span>
                <span>Connected. You can close this tab.</span>
              </div>
              <p className="muted small" style={{ marginTop: 12 }}>Auto-closing in a second…</p>
            </>
          )}

          {status === 'err' && (
            <>
              <div className="error">{err}</div>
              <button onClick={() => window.location.reload()}>Try again</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
